import React from 'react';
import {
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileTextOutlined,
  FileOutlined,
  VideoCameraOutlined,
  AudioOutlined,
} from '@ant-design/icons';

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileTypeFromMime = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'document';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'document';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'document';
  if (mimeType.includes('zip') || mimeType.includes('archive')) return 'archive';
  if (mimeType.startsWith('text/')) return 'document';
  return 'other';
};

export const getFileIcon = (mimeType: string, style?: React.CSSProperties) => {
  const iconStyle = { color: '#1890ff', ...style };
  
  if (mimeType.startsWith('image/')) {
    return React.createElement(FileImageOutlined, { style: { ...iconStyle, color: '#52c41a' } });
  }
  
  if (mimeType === 'application/pdf') {
    return React.createElement(FilePdfOutlined, { style: { ...iconStyle, color: '#ff4d4f' } });
  }
  
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return React.createElement(FileWordOutlined, { style: { ...iconStyle, color: '#1890ff' } });
  }
  
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return React.createElement(FileExcelOutlined, { style: { ...iconStyle, color: '#52c41a' } });
  }
  
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
    return React.createElement(FilePptOutlined, { style: { ...iconStyle, color: '#fa8c16' } });
  }
  
  if (mimeType.includes('zip') || mimeType.includes('archive')) {
    return React.createElement(FileZipOutlined, { style: { ...iconStyle, color: '#722ed1' } });
  }
  
  if (mimeType.startsWith('text/')) {
    return React.createElement(FileTextOutlined, { style: iconStyle });
  }
  
  if (mimeType.startsWith('video/')) {
    return React.createElement(VideoCameraOutlined, { style: { ...iconStyle, color: '#eb2f96' } });
  }
  
  if (mimeType.startsWith('audio/')) {
    return React.createElement(AudioOutlined, { style: { ...iconStyle, color: '#13c2c2' } });
  }
  
  return React.createElement(FileOutlined, { style: iconStyle });
};

export const isPreviewableFile = (mimeType: string): boolean => {
  return (
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'text/plain' ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('audio/')
  );
};

export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();
};

export const isMetadataFile = (filename: string): boolean => {
  return filename.endsWith('.metadata') || filename.includes('/.metadata');
};

export const isSystemFile = (filename: string): boolean => {
  const systemPatterns = [
    '.metadata',
    '.DS_Store',
    'Thumbs.db',
    '.tmp',
    '.cache',
    '~$', // Microsoft Office temp files
  ];
  
  return systemPatterns.some(pattern => filename.includes(pattern));
};

export const generateThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const MAX_SIZE = 200;
      let { width, height } = img;

      if (width > height) {
        if (width > MAX_SIZE) {
          height = (height * MAX_SIZE) / width;
          width = MAX_SIZE;
        }
      } else {
        if (height > MAX_SIZE) {
          width = (width * MAX_SIZE) / height;
          height = MAX_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;
      
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } else {
        reject(new Error('Could not get canvas context'));
      }
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

export const getMimeTypeFromFilename = (filename: string): string => {
  const ext = getFileExtension(filename).toLowerCase();
  
  const mimeTypes: Record<string, string> = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'bmp': 'image/bmp',
    'ico': 'image/x-icon',
    
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    
    // Text
    'txt': 'text/plain',
    'csv': 'text/csv',
    'html': 'text/html',
    'htm': 'text/html',
    'css': 'text/css',
    'js': 'text/javascript',
    'json': 'application/json',
    'xml': 'application/xml',
    
    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'webm': 'video/webm',
    
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'm4a': 'audio/mp4',
    
    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
  };
  
  return mimeTypes[ext] || 'application/octet-stream';
};